import { useEffect, useState } from "react";
import { Box, Button, Modal, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Divider } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { CheckedRedemptions } from "../types/Redeem";
import { processRedemption } from "../services/apiService";
import { formatDate } from "../utils/Validation";
import "./Layout.css";

interface ConfirmRedemptionType {
    open: boolean;
    previewData: CheckedRedemptions[];
    handleClose: (type: string, message?: string) => void;
    action: string;
}

const style = {
    position: "absolute",
    top: "50%",
    left: "55%",
    transform: "translate(-50%, -50%)",
    maxWidth: "80%",
    minWidth: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
};

const ConfirmRedemption = ({
    open,
    handleClose,
    previewData,
    action
}: ConfirmRedemptionType) => {
    const [rows, setRows] = useState<CheckedRedemptions[]>([]);
    const [files, setFiles] = useState<any>({});
    const [showSummary, setShowSummary] = useState(false);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        setRows(previewData);
    }, [files, previewData]);

    const columns: GridColDef<(typeof rows)[number]>[] = [
        {
            field: "id",
            headerName: "ID",
            width: 140,
        },
        {
            field: "points",
            headerName: "Points Redeemed",
            width: 180,
        },
        {
            field: "redemptionStatus",
            headerName: "Status",
            width: 180,
        },
        {
            field: "redemptionMode",
            headerName: "Redemption Mode",
            width: 180,
        },
        {
            field: "accountNumber",
            headerName: "Account Number",
            width: 180,
        },
        {
            field: "ifscCode",
            headerName: "IFSC Code",
            width: 180,
        },
        {
            field: "accountType",
            headerName: "Account Type",
            width: 180,
        },
        {
            field: "accountHolderName",
            headerName: "Account Holder Name",
            width: 180,
        },
        {
            field: "upiId",
            headerName: "UPI ID",
            width: 180,
        },
        {
            field: "comment",
            headerName: "Comment",
            editable: true,
            width: 240,
            cellClassName: "comment-box",
        },
    ];

    const handleSubmit = async () => {
        const hasEmptyComment = rows.some(
            (row) => !row.comment || row.comment.trim() === ""
        );
        if (hasEmptyComment) {
            toast.error("Please enter a comment");
            return;
        }

        if (action === "Approve") {
            setShowSummary(true);
        } else {
            await handleConfirmSubmit();
        }
    };

    const handleConfirmSubmit = async () => {
        try {
            setProcessing(true);
            const payload = rows.map((row) => ({
                id: row.id,
                message: row.comment,
                redemptionStatus: action === "Approve" ? "approved" : "rejected",
            }));

            const response = await processRedemption(payload as any);

            if (response?.message === "Processed successfully") {
                setShowSummary(false);
                handleClose("success", response?.message);
            } else {
                throw new Error("API response unsuccessful");
            }
        } catch (error) {
            console.error("Error processing redemption:", error);
            setShowSummary(false);
            handleClose("error", "Something went wrong, please try again");
        } finally {
            setProcessing(false);
        }
    };

    const processRowUpdate = (newRow: CheckedRedemptions) => {
        setRows((prevRows) =>
            prevRows.map((row) =>
                row.id === newRow.id ? { ...row, comment: newRow.comment } : row
            )
        );
        return newRow;
    };


    const userWiseSummary = rows.reduce((acc: Record<string, number>, row: any) => {
        const name = row.fullName || row.accountHolderName || "Unknown User";
        const amt = Number(row.points) || 0;
        acc[name] = (acc[name] || 0) + amt;
        return acc;
    }, {});

    const totalTransferAmount = Object.values(userWiseSummary).reduce((sum, val) => sum + val, 0);

    return (
        <>
            <Modal open={open} onClose={() => handleClose("")}>
                <Box sx={style}>
                    <span className="confirm-title">Confirm</span>
                    <div style={{ height: 400, width: "100%", overflow: "auto" }}>
                        <DataGrid
                            rows={rows}
                            getRowId={(ele) => ele?.id}
                            columns={columns.map((ele) => ({
                                ...ele,
                                sortable: false,
                                disableColumnMenu: true,
                                resizable: false,
                            }))}
                            processRowUpdate={processRowUpdate}
                            disableRowSelectionOnClick
                        />
                    </div>
                    <div style={{ marginTop: 20, display: "flex", columnGap: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{ backgroundColor: "#03509d" }}
                        >
                            Submit
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => handleClose("")}>
                            Cancel
                        </Button>
                    </div>
                </Box>
            </Modal>

            <Dialog 
                open={showSummary} 
                onClose={() => !processing && setShowSummary(false)} 
                maxWidth="xs" 
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: '#2e3b55', color: 'white', pb: 2 }}>
                    Confirm Bank Transfers
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Please review the bank transfer amounts that will be initiated user-wise upon approval:
                    </Typography>
                    
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                        {Object.entries(userWiseSummary).map(([name, amount]) => (
                            <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                <Typography variant="body1" fontWeight="500">{name}</Typography>
                                <Typography variant="body1" fontWeight="bold">₹{amount}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="bold">Total Amount:</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">₹{totalTransferAmount}</Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={handleConfirmSubmit} 
                        variant="contained" 
                        color="success"
                        disabled={processing}
                    >
                        {processing ? "Processing..." : "Confirm & Approve"}
                    </Button>
                    <Button 
                        onClick={() => setShowSummary(false)} 
                        variant="outlined" 
                        color="inherit"
                        disabled={processing}
                    >
                        Back
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ConfirmRedemption;